import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiPhone, FiClock, FiMapPin, FiArrowRight} = FiIcons;

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({behavior: 'smooth'});
    }
  };

  return (
    <section className="relative text-white overflow-hidden min-h-screen flex items-center">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="https://s3.us-east-2.wasabisys.com/crc-cdn/crc/CRCNU-Hero.mp4" type="video/mp4" />
        </video>
      </div>

      {/* Blue Gradient Overlay at 65% opacity */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/65 via-blue-800/65 to-indigo-900/65 z-10" />

      {/* Additional overlay for better text readability */}
      <div className="absolute inset-0 bg-black/20 z-20" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 z-30">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <motion.div
            initial={{opacity: 0, x: -50}}
            animate={{opacity: 1, x: 0}}
            transition={{duration: 0.8}}
            className="text-center lg:text-left"
          >
            {/* Location Badge */}
            <div className="inline-flex items-center gap-2 bg-blue-700/40 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-blue-500/30">
              <SafeIcon icon={FiMapPin} className="w-4 h-4" />
              <span className="text-sm font-medium">Serving NYC & Hudson Valley</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6 text-shadow-lg">
              AI Voice Receptionists for{' '}
              <span className="text-blue-300">NYC & Hudson Valley</span> Businesses
            </h1>

            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed text-shadow">
              Transform your business communications with our cutting-edge AI voice receptionist technology. Experience 24/7 professional call handling with seamless PBX and VoIP integration. Never miss another important call with our advanced artificial intelligence assistants designed specifically for New York City and Hudson Valley businesses.
            </p>

            {/* Additional SEO Content */}
            <div className="mb-8 bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <p className="text-blue-100 mb-4 leading-relaxed">
                Our advanced AI voice reception system serves businesses across Manhattan, Brooklyn, Queens, the Bronx, and throughout the Hudson Valley region including Poughkeepsie, Kingston, and Newburgh. Whether you're a medical practice, law firm, real estate agency, or small business, our intelligent voice assistant provides professional customer service that enhances your company's reputation.
              </p>
              <p className="text-blue-100 leading-relaxed">
                With natural language processing, multilingual support, and sophisticated call routing capabilities, our AI receptionist integrates seamlessly with your existing phone systems while providing cost-effective scalability for growing businesses.
              </p>
            </div>

            {/* Try Our Sales Bot CTA */}
            <div className="mb-8 bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/30">
              <p className="text-lg font-medium text-white mb-2">
                Experience our AI technology firsthand:
              </p>
              <a
                href="tel:+13322324531"
                className="inline-flex items-center gap-2 text-xl font-bold text-orange-300 hover:text-orange-200 transition-colors"
              >
                <SafeIcon icon={FiPhone} className="w-5 h-5" />
                Try our Sales Bot: +1 332-232-4531
              </a>
            </div>

            {/* Features List */}
            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              {[
                {icon: FiClock, text: '24/7 Availability'},
                {icon: FiPhone, text: 'PBX/VoIP Compatible'},
                {icon: FiPhone, text: 'Smart Call Transfers'},
                {icon: FiPhone, text: 'Multilingual Support'}
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-500/80 backdrop-blur-sm rounded-full flex items-center justify-center border border-blue-400/30">
                    <SafeIcon icon={feature.icon} className="w-4 h-4" />
                  </div>
                  <span className="text-blue-100 text-shadow">{feature.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <motion.button
              whileHover={{scale: 1.05}}
              whileTap={{scale: 0.95}}
              onClick={() => scrollToSection('pricing')}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white px-8 py-4 rounded-full text-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-xl backdrop-blur-sm border border-orange-400/30"
            >
              Get Started Today
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </motion.button>
          </motion.div>

          {/* Visual */}
          <motion.div
            initial={{opacity: 0, x: 50}}
            animate={{opacity: 1, x: 0}}
            transition={{duration: 0.8, delay: 0.2}}
            className="relative"
          >
            <div className="relative bg-gradient-to-br from-blue-600/90 to-indigo-700/90 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-blue-500/30">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 mb-6 border border-white/20">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                    <SafeIcon icon={FiPhone} className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-sm text-blue-200">Incoming Call</div>
                    <div className="font-semibold">Manhattan Law Office</div>
                  </div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-4 border border-white/10">
                  <p className="text-sm text-blue-100">
                    "Hello, thank you for calling Manhattan Law Office. How may I assist you today?"
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <div className="text-2xl font-bold text-green-400">99.9%</div>
                  <div className="text-sm text-blue-200">Uptime</div>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 border border-white/20">
                  <div className="text-2xl font-bold text-blue-300">24/7</div>
                  <div className="text-sm text-blue-200">Available</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;